import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Tabs,
  Tab,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress, 
  Menu,
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { TASK_STATUS_OPTIONS, TASK_PRIORITY_OPTIONS, TASK_TYPE, getPriorityColor, getPriorityLabel, getStatusColor, getStatusLabel, getTaskTypeColor, getTaskTypeLabel } from '../../models/TaskDTO';
import * as Yup from 'yup';
import { TaskDTO, LeadDTO, Lead, TaskHistoryDTO } from '../../api/generated/requests/types.gen';
import { TasksService, LeadCrudService, LeadTaskManagementService, LeadAssignmentService } from '../../api/generated/requests/services.gen';
import { CommentsService } from '../../api/generated/requests/services.gen';
import { MinIoFileStorageService } from '../../api/generated/requests/services.gen';
import { useUserData, getUserFullName } from '../../hooks/useUserData';
import { useRelatedFiles, useFileMutations } from '../../hooks/useFileData';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { CommentDTO, FileMetadataDTO } from '../../api/generated/requests/types.gen';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachFile as AttachFileIcon,
  Comment as CommentIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Create as CreateIcon,
  Assignment as AssignmentIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import LeadContactDialog from '../Leads/LeadContactDialog';
import LeadDetailDialog from '../Leads/LeadDetailDialog';
import ContactSearchMenu from '../Common/ContactSearchMenu';
import UserSearchMenu from '../Common/UserSearchMenu';
import FileList from '../Common/FileList';
import { MentionInput } from '../Common/MentionInput';
import { MentionText } from '../Common/MentionText';
import TaskStatusTimeline from './TaskStatusTimeline';


interface TaskDetailDialogProps {
  open: boolean;
  taskId: number | null;
  onClose: () => void;
  onTaskUpdated?: () => void;
  initialTab?: number;
  highlightCommentId?: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-detail-tabpanel-${index}`}
      aria-labelledby={`task-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0.5, pt: 2, height: '450px', overflow: 'auto' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `task-detail-tab-${index}`,
    'aria-controls': `task-detail-tabpanel-${index}`,
  };
}

interface TaskHistoryProps {
  taskId: number;
}

function TaskHistory({ taskId }: TaskHistoryProps) {
  const { data: history, isLoading, error } = useQuery({
    queryKey: ['taskHistory', taskId],
    queryFn: () => TasksService.getApiTasksByTaskIdHistory({ taskId }),
    enabled: !!taskId,
  });


  const { domainUsers } = useUserData();

  const getUserFullNameById = (userId?: string) => {
    if (!userId) return 'Nieprzypisane';
    const user = domainUsers.find(u => u.id === userId);
    return getUserFullName(user);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd MMM yyyy HH:mm', { locale: pl });
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'CREATE': return 'Utworzono';
      case 'UPDATE': return 'Zaktualizowano';
      case 'DELETE': return 'Usunięto';
      default: return action;
    }
  };

  const getChangedFieldsText = (changedFields: string[] | undefined) => {
    if (!changedFields || changedFields.length === 0) return '-';
    
    const fieldLabels: { [key: string]: string } = {
      'title': 'Tytuł',
      'description': 'Opis',
      'status': 'Status',
      'priority': 'Priorytet',
      'taskType': 'Typ zadania',
      'assignedToUserId': 'Przypisany użytkownik',
      'startDate': 'Data rozpoczęcia',
      'endDate': 'Data zakończenia'
    };

    return changedFields.map(field => fieldLabels[field] || field).join(', ');
  };

  const getChangedValuesText = (entry: TaskHistoryDTO) => {
    if (!entry.changedFields || entry.changedFields.length === 0) return '-';
    
    // Parse taskData JSON to get current values
    let currentTaskData: any = {};
    try {
      if (entry.taskData) {
        currentTaskData = JSON.parse(entry.taskData);
      }
    } catch (error) {
      console.error('Error parsing taskData:', error);
      return '-';
    }

    const formatValue = (field: string, value: any, isUser: boolean = false) => {
      if (value === null || value === undefined) return 'Brak';
      
      switch (field) {
        case 'status':
          return getStatusLabel(value);
        case 'priority':
          return getPriorityLabel(value);
        case 'taskType':
          return getTaskTypeLabel(value);
        case 'assignedToUserId':
          return isUser ? getUserFullNameById(value) : value;
        case 'startDate':
        case 'endDate':
          return value ? format(new Date(value), 'dd MMM yyyy HH:mm', { locale: pl }) : 'Brak';
        case 'title':
        case 'description':
          return value ? value : 'Brak';
        default:
          return value?.toString() || 'Brak';
      }
    };

    const changes = entry.changedFields.map(field => {
      if (field === 'assignedToUserId' && entry.previousAssignedToUserId !== undefined && entry.newAssignedToUserId !== undefined) {
        // Special handling for user assignment changes
        const previousUser = formatValue(field, entry.previousAssignedToUserId, true);
        const newUser = formatValue(field, entry.newAssignedToUserId, true);
        return `${previousUser} → ${newUser}`;
      } else {
        // For other fields, show current value (we don't have previous values)
        const currentValue = formatValue(field, currentTaskData[field], field === 'assignedToUserId');
        return currentValue;
      }
    });

    return changes.join(', ');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">
          Nie udało się załadować historii zadania
        </Typography>
      </Box>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Brak historii dla tego zadania
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Data</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Akcja</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Użytkownik</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Zmienione pola</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Wartości</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((entry) => (
            <TableRow key={entry.id} hover>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {entry.changedAt ? formatDate(entry.changedAt) : '-'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                <Chip
                  label={getActionLabel(entry.action || '')}
                  size="small"
                  color={
                    entry.action === 'CREATE' ? 'success' :
                    entry.action === 'UPDATE' ? 'primary' :
                    entry.action === 'DELETE' ? 'error' : 'default'
                  }
                  sx={{ fontSize: '0.65rem', height: '20px' }}
                />
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {getUserFullNameById(entry.changedByUserId)}
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {getChangedFieldsText(entry.changedFields)}
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {getChangedValuesText(entry)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
Yup.object({
  title: Yup.string()
      .required('Tytuł jest wymagany')
      .max(255, 'Tytuł nie może przekraczać 255 znaków'),
  description: Yup.string()
      .max(5000, 'Opis nie może przekraczać 5000 znaków'),
  status: Yup.string().required('Status jest wymagany'),
  priority: Yup.string().oneOf(['A', 'B', 'C', 'D', 'E']).required('Priorytet jest wymagany'),
  taskType: Yup.string().required('Typ zadania jest wymagany'),
  startDate: Yup.date()
      .required('Termin jest wymagany'),
  endDate: Yup.date().when('taskType', {
    is: 'MEETING',
    then: (schema) => schema
      .required('Termin do jest wymagany')
      .min(Yup.ref('startDate'), 'Termin do nie może być wcześniejszy niż termin od'),
    otherwise: (schema) => schema.notRequired()
  }),
});
const TaskDetailDialog: React.FC<TaskDetailDialogProps> = ({ open, taskId, onClose, onTaskUpdated = () => {}, initialTab = 0, highlightCommentId = null }) => {
  const [task, setTask] = useState<TaskDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(initialTab);
  const [comments, setComments] = useState<CommentDTO[]>([]);
  const [newComment, setNewComment] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const { files, isLoading: loadingFiles, refetch: refetchFiles } = useRelatedFiles(taskId as number, 'TASK');
  const { uploadFile, deleteFile, restoreFile, downloadFile, isUploading, isDeleting } = useFileMutations();
  const queryClient = useQueryClient();
  const [loadingComments, setLoadingComments] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Record<number, boolean>>({});
  const [showAllComments, setShowAllComments] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const [, setUserSearchTerm] = useState('');
  const [assignUserMenuAnchor, setAssignUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [assignLeadMenuAnchor, setAssignLeadMenuAnchor] = useState<HTMLElement | null>(null);
  const [openLeadDialog, setOpenLeadDialog] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [openLeadDetailDialog, setOpenLeadDetailDialog] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const clickTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Inline editing states
  const [titleEditMode, setTitleEditMode] = useState(false);
  const [descriptionEditMode, setDescriptionEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<HTMLElement | null>(null);
  const [taskTypeMenuAnchor, setTaskTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [pendingStartDate, setPendingStartDate] = useState<Date | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  const startDateChipRef = React.useRef<HTMLDivElement>(null);
  const endDateChipRef = React.useRef<HTMLDivElement>(null);


  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const fetchComments = useCallback(async () => {
    if (!taskId) return;

    setLoadingComments(true);
    try {
      const commentsData = await CommentsService.getApiComments({
        objectId: taskId,
        objectType: 'TASK'
      });
      // Sort comments by date in descending order (newest first)
      const sortedComments = [...commentsData].sort((a, b) => {
        const dateA = new Date(a.createdAt || '');
        const dateB = new Date(b.createdAt || '');
        return dateB.getTime() - dateA.getTime();
      });

      setComments(sortedComments);

      // Reset expanded comments state
      const initialExpandedState: Record<number, boolean> = {};
      sortedComments.forEach(comment => {
        if (comment.id) {
          initialExpandedState[comment.id] = true; // Set to true to make comments expanded by default
        }
      });
      setExpandedComments(initialExpandedState);

      // Reset show all comments state
      setShowAllComments(false);
    } catch (error) {
      console.error('Error fetching comments:', error);
      enqueueSnackbar('Nie udało się pobrać komentarzy', { variant: 'error' });
    } finally {
      setLoadingComments(false);
    }
  }, [taskId, enqueueSnackbar]);

  // Comment highlighting effect when navigating from search
  useEffect(() => {
    if (highlightCommentId && comments.length > 0 && tabValue === 1) {
      const expandCommentsUntilVisible = async () => {
        let attempts = 0;
        const maxAttempts = 10; // Zabezpieczenie przed nieskończoną pętlą
        
        while (attempts < maxAttempts) {
          // Sprawdź czy komentarz jest już widoczny w DOM
          const commentElement = document.querySelector(`[data-comment-id="${highlightCommentId}"]`);
          
          if (commentElement) {
            // Komentarz znaleziony - podświetl go
            setTimeout(() => {
              commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              commentElement.classList.add('highlighted-comment');
              setTimeout(() => {
                commentElement.classList.remove('highlighted-comment');
              }, 2500);
            }, 100);
            break;
          }
          
          // Jeśli komentarz nie jest widoczny, sprawdź czy jest przycisk "Zobacz więcej"
          if (!showAllComments) {
            setShowAllComments(true);
            // Poczekaj na rerenderowanie po ustawieniu showAllComments
            await new Promise(resolve => setTimeout(resolve, 300));
            attempts++;
          } else {
            // showAllComments jest już true, ale komentarz nie jest widoczny
            // Prawdopodobnie komentarz nie istnieje
            console.warn(`Komentarz o ID ${highlightCommentId} nie został znaleziony`);
            break;
          }
        }
        
        if (attempts >= maxAttempts) {
          console.warn(`Przekroczono maksymalną liczbę prób (${maxAttempts}) wyszukiwania komentarza o ID ${highlightCommentId}`);
        }
      };
      
      // Dodaj małe opóźnienie na wyrenderowanie komponentu
      const timeoutId = setTimeout(() => {
        expandCommentsUntilVisible();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [highlightCommentId, comments, tabValue, showAllComments]);

  // Add CSS styles for comment highlighting
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .highlighted-comment {
        background-color: #fff3cd !important;
        border: 2px solid #ffc107 !important;
        animation: commentHighlightPulse 2.5s ease-in-out;
      }
      
      @keyframes commentHighlightPulse {
        0% { background-color: #fff3cd; }
        50% { background-color: #ffeaa7; }
        100% { background-color: #fff3cd; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);



  // Fetch user data using optimized hook
  const { currentUser, domainUsers, isLoading: loadingUsers } = useUserData();

  // Funkcja do przypisywania zadania do użytkownika
  const handleAssignUser = async (userId: string) => {
    if (!taskId || !task) return;

    try {
      // Wyślij żądanie przypisania zadania do użytkownika
      const response = await TasksService.putApiTasksByIdAssignByKeycloakUserId({
        id: taskId,
        keycloakUserId: userId
      });

      // Preserve existing task data, especially leads array, when updating user assignment
      setTask({
        ...task,
        ...response,
        leads: task.leads || []
      });
      enqueueSnackbar('Zadanie zostało przypisane do użytkownika', { variant: 'success' });
      onTaskUpdated();
      // Invalidate task history cache to refresh the Historia tab
      queryClient.invalidateQueries({ queryKey: ['taskHistory', taskId] });
    } catch (error) {
      console.error('Błąd podczas przypisywania zadania do użytkownika:', error);
      enqueueSnackbar('Nie udało się przypisać zadania do użytkownika', { variant: 'error' });
    } finally {
      setAssignUserMenuAnchor(null);
      setUserSearchTerm('');
    }
  };

  // Funkcja do usuwania przypisania zadania
  const handleUnassignUser = async () => {
    if (!taskId || !task) return;

    try {
      // Wyślij żądanie usunięcia przypisania zadania
      const response = await TasksService.putApiTasksByIdUnassign({
        id: taskId
      });

      // Preserve existing task data, especially leads array, when updating user assignment
      setTask({
        ...task,
        ...response,
        leads: task.leads || []
      });
      enqueueSnackbar('Przypisanie zadania zostało usunięte', { variant: 'success' });
      onTaskUpdated();
      // Invalidate task history cache to refresh the Historia tab
      queryClient.invalidateQueries({ queryKey: ['taskHistory', taskId] });
    } catch (error) {
      console.error('Błąd podczas usuwania przypisania zadania:', error);
      enqueueSnackbar('Nie udało się usunąć przypisania zadania', { variant: 'error' });
    } finally {
      setAssignUserMenuAnchor(null);
    }
  };

  // Funkcja pomocnicza do uzyskania inicjałów użytkownika
  const getUserInitials = (userId?: string) => {
    if (!userId) return '';

    const user = domainUsers.find(u => u.id === userId);
    if (!user) return '';

    const firstName = user.firstName || '';
    const lastName = user.lastName || '';

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Funkcja pomocnicza do uzyskania pełnego imienia i nazwiska użytkownika
  const getUserFullNameById = (userId?: string) => {
    if (!userId) return 'Nieprzypisane';

    const user = domainUsers.find(u => u.id === userId);
    return getUserFullName(user);
  };

// Funkcja do przypisywania kontaktu do zadania
  const handleAssignLead = async (leadId: number) => {
    if (!taskId || !task) return;

    // Sprawdź czy kontakt jest już przypisany do zadania
    const isAlreadyAssigned = task.leads?.some(lead => lead.id === leadId);
    if (isAlreadyAssigned) {
      enqueueSnackbar('Ten kontakt jest już przypisany do zadania', { variant: 'warning' });
      setAssignLeadMenuAnchor(null);
      return;
    }

    try {
      // Wyślij żądanie przypisania kontaktu do zadania używając nowego API
      await LeadAssignmentService.postApiLeadTasksAssignLeadsToTask({
        requestBody: {
          taskId: taskId,
          leadIds: [leadId],
          assignedByUserId: currentUser?.id || ''
        }
      });

      // Pobierz dane kontaktu do dodania do lokalnego stanu
      const leadData = await LeadCrudService.getApiLeadContactsById({ id: leadId });
      
      // Zaktualizuj lokalny stan zadania bez przeładowywania całego dialogu
      const updatedTask = {
        ...task,
        leads: [...(task.leads || []), leadData]
      };
      setTask(updatedTask);
      
      enqueueSnackbar('Kontakt został przypisany do zadania', { variant: 'success' });
      onTaskUpdated();
    } catch (error) {
      console.error('Błąd podczas przypisywania kontaktu do zadania:', error);
      enqueueSnackbar('Nie udało się przypisać kontaktu do zadania', { variant: 'error' });
    } finally {
      setAssignLeadMenuAnchor(null);
    }
  };

  // Funkcja do usuwania przypisania kontaktu do zadania
  const handleUnassignLead = async (leadId: number) => {
    if (!taskId || !task) return;

    try {
      // Wyślij żądanie usunięcia przypisania kontaktu do zadania używając nowego API
      await LeadTaskManagementService.deleteApiLeadTasksDetachLeadsFromTask({
        requestBody: {
          taskId: taskId,
          leadIds: [leadId]
        }
      });

      // Zaktualizuj lokalny stan zadania bez przeładowywania całego dialogu
      const updatedTask = {
        ...task,
        leads: (task.leads || []).filter(lead => lead.id !== leadId)
      };
      setTask(updatedTask);
      
      enqueueSnackbar('Przypisanie kontaktu do zadania zostało usunięte', { variant: 'success' });
      onTaskUpdated();
    } catch (error) {
      console.error('Błąd podczas usuwania przypisania kontaktu do zadania:', error);
      enqueueSnackbar('Nie udało się usunąć przypisania kontaktu do zadania', { variant: 'error' });
    }
  };

  // Handler functions for LeadContactDialog
  const handleOpenLeadDialog = () => {
    setOpenLeadDialog(true);
  };

  const handleCloseLeadDialog = () => {
    setOpenLeadDialog(false);
    // Also close the ContactSearchMenu when LeadContactDialog is closed
    setAssignLeadMenuAnchor(null);
  };

  const handleSaveLead = async (lead: LeadDTO) => {
    setIsSubmittingLead(true);
    try {
      const newLead = await LeadCrudService.postApiLeadContacts({
        requestBody: lead
      });
      enqueueSnackbar('Kontakt został utworzony', { variant: 'success' });
      handleCloseLeadDialog();


      // Automatycznie przypisz nowo utworzony kontakt do zadania używając nowego API
      if (newLead && newLead.id && taskId && task) {
        try {
          await LeadAssignmentService.postApiLeadTasksAssignLeadsToTask({
            requestBody: {
              taskId: taskId,
              leadIds: [newLead.id],
              assignedByUserId: currentUser?.id || ''
            }
          });
          
          // Zaktualizuj lokalny stan zadania bez przeładowywania całego dialogu
          const updatedTask = {
            ...task,
            leads: [...(task.leads || []), newLead]
          };
          setTask(updatedTask);
          
          enqueueSnackbar('Kontakt został automatycznie przypisany do zadania', { variant: 'success' });
          onTaskUpdated();
        } catch (assignError) {
          console.error('Błąd podczas automatycznego przypisywania kontaktu:', assignError);
          enqueueSnackbar('Kontakt został utworzony, ale nie udało się go automatycznie przypisać', { variant: 'warning' });
        }
      }

      // Wyślij globalne zdarzenie do odświeżenia listy leadów
      window.dispatchEvent(new CustomEvent('refresh-leads'));

      return newLead;
    } catch (error) {
      console.error('Błąd podczas tworzenia kontaktu:', error);
      enqueueSnackbar('Wystąpił błąd podczas tworzenia kontaktu', { variant: 'error' });
    } finally {
      setIsSubmittingLead(false);
    }
  };


  const fetchTaskDetails = useCallback(async () => {
    if (!taskId) return;

    setLoading(true);
    try {
      const taskData = await TasksService.getApiTasksById({ id: taskId });
      setTask(taskData);
      setEditedTitle(taskData.title || '');
      setEditedDescription(taskData.description || '');

      // Fetch comments
      await fetchComments();
    } catch (error) {
      console.error('Error fetching task details:', error);
      enqueueSnackbar('Nie udało się pobrać szczegółów zadania', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [taskId, fetchComments, enqueueSnackbar]);

  // Fetch task details when dialog opens
  useEffect(() => {
    if (open && taskId) {
      fetchTaskDetails();
    }
  }, [open, taskId, fetchTaskDetails]);

  // Update tabValue when initialTab changes
  useEffect(() => {
    setTabValue(initialTab);
  }, [initialTab]);


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Inline editing handlers
  const handleTitleClick = () => {
      setTitleEditMode(true);
      setEditedTitle(task?.title || '');
  };

  const handleDescriptionClick = () => {
      setDescriptionEditMode(true);
      setEditedDescription(task?.description || '');
  };

  const handleSaveTitle = async () => {
    if (!taskId || !task) return;

    // Validate title length
    if (editedTitle.trim() === '') {
      enqueueSnackbar('Tytuł nie może być pusty', { variant: 'error' });
      return;
    }

    if (editedTitle.length > 255) {
      enqueueSnackbar('Tytuł nie może przekraczać 255 znaków', { variant: 'error' });
      return;
    }

    try {
      const updatedTask = { ...task, title: editedTitle.trim() };
      const response = await TasksService.putApiTasksById({
        id: taskId,
        requestBody: updatedTask
      });

      // Preserve existing leads when updating task
      const updatedTaskWithLeads = {
        ...response,
        leads: task.leads || []
      };
      setTask(updatedTaskWithLeads);
      setTitleEditMode(false);
      enqueueSnackbar('Tytuł zadania został zaktualizowany', { variant: 'success' });
      onTaskUpdated();
      // Invalidate task history cache to refresh the Historia tab
      queryClient.invalidateQueries({ queryKey: ['taskHistory', taskId] });
    } catch (error) {
      console.error('Error updating task title:', error);
      enqueueSnackbar('Nie udało się zaktualizować tytułu zadania', { variant: 'error' });
    }
  };

  const handleSaveDescription = async () => {
    if (!taskId || !task) return;

    // Validate description length
    if (editedDescription.length > 5000) {
      enqueueSnackbar('Opis nie może przekraczać 5000 znaków', { variant: 'error' });
      return;
    }

    try {
      const updatedTask = { ...task, description: editedDescription };
      const response = await TasksService.putApiTasksById({
        id: taskId,
        requestBody: updatedTask
      });

      // Preserve existing leads when updating task
      const updatedTaskWithLeads = {
        ...response,
        leads: task.leads || []
      };
      setTask(updatedTaskWithLeads);
      setDescriptionEditMode(false);
      enqueueSnackbar('Opis zadania został zaktualizowany', { variant: 'success' });
      onTaskUpdated();
      // Invalidate task history cache to refresh the Historia tab
      queryClient.invalidateQueries({ queryKey: ['taskHistory', taskId] });
    } catch (error) {
      console.error('Error updating task description:', error);
      enqueueSnackbar('Nie udało się zaktualizować opisu zadania', { variant: 'error' });
    }
  };

  const handleCancelTitleEdit = () => {
    setTitleEditMode(false);
    setEditedTitle(task?.title || '');
  };

  const handleCancelDescriptionEdit = () => {
    setDescriptionEditMode(false);
    setEditedDescription(task?.description || '');
  };

  const handleStatusChange = async (newStatus: 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE') => {
    if (!taskId || !task) return;

    try {
      const updatedTask = { ...task, status: newStatus };
      const response = await TasksService.putApiTasksById({
        id: taskId,
        requestBody: updatedTask
      });

      // Preserve existing leads when updating task
      const updatedTaskWithLeads = {
        ...response,
        leads: task.leads || []
      };
      setTask(updatedTaskWithLeads);
      setStatusMenuAnchor(null);
      enqueueSnackbar('Status zadania został zmieniony', { variant: 'success' });
      onTaskUpdated();
      // Invalidate task history cache to refresh the Historia tab
      queryClient.invalidateQueries({ queryKey: ['taskHistory', taskId] });
    } catch (error) {
      console.error('Error updating task status:', error);
      enqueueSnackbar('Nie udało się zmienić statusu zadania', { variant: 'error' });
    }
  };

  const handlePriorityChange = async (newPriority: 'A' | 'B' | 'C' | 'D' | 'E') => {
    if (!taskId || !task) return;

    try {
      const updatedTask = { ...task, priority: newPriority };
      const response = await TasksService.putApiTasksById({
        id: taskId,
        requestBody: updatedTask
      });

      // Preserve existing leads when updating task
      const updatedTaskWithLeads = {
        ...response,
        leads: task.leads || []
      };
      setTask(updatedTaskWithLeads);
      setPriorityMenuAnchor(null);
      enqueueSnackbar('Priorytet zadania został zmieniony', { variant: 'success' });
      onTaskUpdated();
      // Invalidate task history cache to refresh the Historia tab
      queryClient.invalidateQueries({ queryKey: ['taskHistory', taskId] });
    } catch (error) {
      console.error('Error updating task priority:', error);
      enqueueSnackbar('Nie udało się zmienić priorytetu zadania', { variant: 'error' });
    }
  };

  const handleTaskTypeChange = async (newTaskType: 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER') => {
    if (!taskId || !task) return;

    try {
      const updatedTask = { ...task, taskType: newTaskType };
      const response = await TasksService.putApiTasksById({
        id: taskId,
        requestBody: updatedTask
      });

      // Preserve existing leads when updating task
      const updatedTaskWithLeads = {
        ...response,
        leads: task.leads || []
      };
      setTask(updatedTaskWithLeads);
      setTaskTypeMenuAnchor(null);
      enqueueSnackbar('Typ zadania został zmieniony', { variant: 'success' });
      onTaskUpdated();
      // Invalidate task history cache to refresh the Historia tab
      queryClient.invalidateQueries({ queryKey: ['taskHistory', taskId] });
    } catch (error) {
      console.error('Error updating task type:', error);
      enqueueSnackbar('Nie udało się zmienić typu zadania', { variant: 'error' });
    }
  };

  const handleStartDateAccept = async (newDate: Date | null) => {

    if (!taskId || !task || !newDate) {
      return;
    }

    const currentEndDate = task.endDate ? new Date(task.endDate) : null;
    const isMeeting = task.taskType === 'MEETING';

    // For non-meeting types, automatically calculate endDate as startDate + 15 minutes
    if (!isMeeting) {
      const autoEndDate = new Date(newDate);
      autoEndDate.setMinutes(autoEndDate.getMinutes() + 15);

      try {
        const updatedTask = { 
          ...task, 
          startDate: newDate.toISOString(),
          endDate: autoEndDate.toISOString()
        };
        const response = await TasksService.putApiTasksById({
          id: taskId,
          requestBody: updatedTask
        });

        // Preserve existing leads when updating task
        const updatedTaskWithLeads = {
          ...response,
          leads: task.leads || []
        };
        setTask(updatedTaskWithLeads);
        setStartDatePickerOpen(false);
        setTempStartDate(null);
        enqueueSnackbar('Termin został zmieniony', { variant: 'success' });
        onTaskUpdated();
      } catch (error) {
        console.error('❌ Error updating task date:', error);
        enqueueSnackbar('Nie udało się zmienić terminu', { variant: 'error' });
      }
      return;
    }

    // For meeting types, keep the original logic
    // Check if start date is later than end date
    if (currentEndDate && newDate > currentEndDate) {
      enqueueSnackbar('Termin od nie może być późniejszy niż termin do', { variant: 'error' });
      setStartDatePickerOpen(false);

      // Store the pending start date
      setPendingStartDate(newDate);

      // Set default end date to start date + 1 hour for meetings
      const defaultEndDate = new Date(newDate);
      defaultEndDate.setHours(defaultEndDate.getHours() + 1);
      setTempEndDate(defaultEndDate);

      // Open end date picker
      setTimeout(() => {
        setEndDatePickerOpen(true);
      }, 100);
      return;
    }

    try {
      const updatedTask = { ...task, startDate: newDate.toISOString() };
      const response = await TasksService.putApiTasksById({
        id: taskId,
        requestBody: updatedTask
      });

      // Preserve existing leads when updating task
      const updatedTaskWithLeads = {
        ...response,
        leads: task.leads || []
      };
      setTask(updatedTaskWithLeads);
      setStartDatePickerOpen(false);
      setTempStartDate(null);
      enqueueSnackbar('Termin rozpoczęcia został zmieniony', { variant: 'success' });
      onTaskUpdated();
    } catch (error) {
      console.error('❌ Error updating task start date:', error);
      enqueueSnackbar('Nie udało się zmienić terminu rozpoczęcia', { variant: 'error' });
    }
  };

  const handleEndDateAccept = async (newDate: Date | null) => {

    if (!taskId || !task || !newDate) {
      return;
    }

    const startDateToCheck = pendingStartDate || (task.startDate ? new Date(task.startDate) : null);

    // Check if end date is earlier than start date
    if (startDateToCheck && newDate < startDateToCheck) {
      enqueueSnackbar('Termin do nie może być wcześniejszy niż termin od', { variant: 'error' });
      return;
    }

    await updateEndDate(newDate);
  };

  const updateEndDate = async (newDate: Date) => {
    if (!taskId || !task) return;

    try {
      // If we have a pending start date, update both dates
      const updatedTask = pendingStartDate 
        ? { ...task, startDate: pendingStartDate.toISOString(), endDate: newDate.toISOString() }
        : { ...task, endDate: newDate.toISOString() };

      const response = await TasksService.putApiTasksById({
        id: taskId,
        requestBody: updatedTask
      });


      // Preserve existing leads when updating task
      const updatedTaskWithLeads = {
        ...response,
        leads: task.leads || []
      };
      setTask(updatedTaskWithLeads);
      setEndDatePickerOpen(false);
      setTempEndDate(null);
      setPendingStartDate(null);

      const message = pendingStartDate 
        ? 'Terminy rozpoczęcia i zakończenia zostały zmienione'
        : 'Termin zakończenia został zmieniony';
      enqueueSnackbar(message, { variant: 'success' });
      onTaskUpdated();
    } catch (error) {
      console.error('❌ Error updating task end date:', error);
      enqueueSnackbar('Nie udało się zmienić terminu zakończenia', { variant: 'error' });
    }
  };

  const handleStartDateChange = (newDate: Date | null) => {
    setTempStartDate(newDate);
  };

  const handleEndDateChange = (newDate: Date | null) => {
    setTempEndDate(newDate);
  };

  const handleAddComment = async () => {
    if (!taskId || !newComment.trim()) return;

    try {
      const commentData: CommentDTO = {
        description: newComment,
        objectId: taskId,
        objectType: 'TASK',
        mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined
      };

      const response = await CommentsService.postApiComments({
        requestBody: commentData
      });

      setComments([response, ...comments]);

      // Ensure the new comment is expanded by default
      if (response.id) {
        setExpandedComments(prev => ({
          ...prev,
          [response.id!]: true
        }));
      }

      setNewComment('');
      setMentionedUserIds([]);
      enqueueSnackbar('Komentarz został dodany', { variant: 'success' });
    } catch (error) {
      console.error('Error adding comment:', error);
      enqueueSnackbar('Nie udało się dodać komentarza', { variant: 'error' });
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await CommentsService.deleteApiCommentsById({ id: commentId });
      setComments(comments.filter(comment => comment.id !== commentId));
      enqueueSnackbar('Komentarz został usunięty', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting comment:', error);
      enqueueSnackbar('Nie udało się usunąć komentarza', { variant: 'error' });
    }
  };

  const toggleCommentExpand = (commentId: number) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const handleEditComment = (comment: CommentDTO) => {
    setEditingCommentId(comment.id || null);
    setEditedCommentText(comment.description || '');
  };

  const handleSaveComment = async () => {
    if (!editingCommentId || !editedCommentText.trim()) return;

    try {
      const updatedComment = await CommentsService.putApiCommentsById({
        id: editingCommentId,
        requestBody: {
          description: editedCommentText,
          objectId: taskId || 0,
          objectType: 'TASK'
        }
      });

      setComments(comments.map(comment => 
        comment.id === editingCommentId ? updatedComment : comment
      ));
      setEditingCommentId(null);
      setEditedCommentText('');
      enqueueSnackbar('Komentarz został zaktualizowany', { variant: 'success' });
    } catch (error) {
      console.error('Error updating comment:', error);
      enqueueSnackbar('Nie udało się zaktualizować komentarza', { variant: 'error' });
    }
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditedCommentText('');
  };

  const handleCommentChange = (value: string, mentionedIds: string[]) => {
    setNewComment(value);
    setMentionedUserIds(mentionedIds);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0 || !taskId) return;

    const file = event.target.files[0];
    await handleUploadFile(file);
  };

  const handleUploadFile = async (file: File) => {
    if (!taskId) return;

    try {
      await uploadFile({ 
        formData: { file: file },
        relatedObjectId: taskId.toString(),
        relatedObjectType: 'TASK'
      });
      refetchFiles();
      
      // Refresh storage usage in sidebar
      queryClient.invalidateQueries({ queryKey: ['MinIoFileStorageServiceGetApiFilesStorageInfo'] });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Only handle the first file for simplicity
      handleUploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleDownloadFile = async (filename: string, originalFilename?: string) => {
    await downloadFile(filename, originalFilename);
  };

  const handleDeleteFile = async (file: FileMetadataDTO) => {
    deleteFile(file);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Status Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            maxHeight: 180,
            width: 200,
          },
        }}
      >
        {TASK_STATUS_OPTIONS.map((option) => {
          const isCurrentStatus = task?.status === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handleStatusChange(option.value as 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE')}
              sx={{
                backgroundColor: isCurrentStatus ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getStatusColor(option.value),
                  borderRadius: '50%'
                }}
              />
              <Typography sx={{ fontSize: '0.8rem' }}>{option.label}</Typography>
              {isCurrentStatus && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Priority Menu */}
      <Menu
        anchorEl={priorityMenuAnchor}
        open={Boolean(priorityMenuAnchor)}
        onClose={() => setPriorityMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            maxHeight: 180,
            width: 200,
          },
        }}
      >
        {TASK_PRIORITY_OPTIONS.map((option) => {
          const isCurrentPriority = task?.priority === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handlePriorityChange(option.value as 'A' | 'B' | 'C' | 'D' | 'E')}
              sx={{
                backgroundColor: isCurrentPriority ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getPriorityColor(option.value as 'A' | 'B' | 'C' | 'D' | 'E'),
                  borderRadius: '50%'
                }}
              />
              <Typography sx={{ fontSize: '0.8rem' }}>{option.label}</Typography>
              {isCurrentPriority && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Task Type Menu */}
      <Menu
        anchorEl={taskTypeMenuAnchor}
        open={Boolean(taskTypeMenuAnchor)}
        onClose={() => setTaskTypeMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            maxHeight: 180,
            width: 200,
          },
        }}
      >
        {TASK_TYPE.map((option) => {
          const isCurrentTaskType = task?.taskType === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handleTaskTypeChange(option.value as 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER')}
              sx={{
                backgroundColor: isCurrentTaskType ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getTaskTypeColor(option.value),
                  borderRadius: '50%'
                }}
              />
              <Typography sx={{ fontSize: '0.8rem' }}>{option.label}</Typography>
              {isCurrentTaskType && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Hidden DateTimePickers that open directly */}
      <Box sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
        <DateTimePicker
          label="Termin od"
          value={tempStartDate || (task?.startDate ? new Date(task.startDate) : new Date())}
          onChange={handleStartDateChange}
          onAccept={handleStartDateAccept}
          open={startDatePickerOpen}
          onClose={() => {
            setStartDatePickerOpen(false);
            setTempStartDate(null);
          }}
          slotProps={{
            textField: {
              size: 'small'
            },
            popper: {
              anchorEl: startDateChipRef.current,
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
              sx: {
                '& .MuiPaper-root': {
                  transform: 'scale(0.85)',
                  transformOrigin: 'top left'
                }
              }
            }
          }}
        />
        <DateTimePicker
          label="Termin do"
          value={tempEndDate || (task?.endDate ? new Date(task.endDate) : new Date())}
          onChange={handleEndDateChange}
          onAccept={handleEndDateAccept}
          minDate={
            pendingStartDate
                ? new Date(pendingStartDate)
                : (task?.startDate
                    ? new Date(task.startDate)
                    : new Date())
          }
          open={endDatePickerOpen}
          onClose={() => {
            // If we have a pendingStartDate and the picker is closing, 
            // we need to make the API call with the current value
            if (pendingStartDate && taskId && task) {
              const endDateToUse = tempEndDate || (task?.endDate ? new Date(task.endDate) : new Date());

              // Validate that end date is not earlier than start date
              if (endDateToUse >= pendingStartDate) {
                updateEndDate(endDateToUse);
              } else {
                enqueueSnackbar('Termin do nie może być wcześniejszy niż termin od', { variant: 'error' });
                setPendingStartDate(null);
              }
            }

            setEndDatePickerOpen(false);
            setTempEndDate(null);
            setPendingStartDate(null);
          }}
          slotProps={{
            textField: {
              size: 'small'
            },
            popper: {
              anchorEl: endDateChipRef.current,
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
              sx: {
                '& .MuiPaper-root': {
                  transform: 'scale(0.85)',
                  transformOrigin: 'top left'
                }
              }
            }
          }}
        />
      </Box>

      {/* Menu wyboru użytkownika */}
      <UserSearchMenu
        anchorEl={assignUserMenuAnchor}
        open={Boolean(assignUserMenuAnchor)}
        onClose={() => setAssignUserMenuAnchor(null)}
        onSelectUser={(userId) => {
          if (userId === undefined) {
            handleUnassignUser();
          } else {
            handleAssignUser(userId);
          }
        }}
        users={domainUsers}
        isLoading={loadingUsers}
        currentUserId={task?.assignedToUserId}
        getUserDisplayName={getUserFullNameById}
      />

      {/* Menu wyboru kontaktu */}
      <ContactSearchMenu
        anchorEl={assignLeadMenuAnchor}
        open={Boolean(assignLeadMenuAnchor)}
        onClose={() => setAssignLeadMenuAnchor(null)}
        onSelectContact={(leadId) => {
          if (leadId === undefined) {
            // For the new system, we need a leadId to unassign
            return;
          } else {
            handleAssignLead(leadId);
          }
        }}
        currentContactId={undefined}
        onAddNewContact={handleOpenLeadDialog}
      />


      <DialogTitle sx={{ py: 1, fontSize: '0.9rem', fontWeight: 600 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="inherit">
            Zadanie ID {task.prefixId} {task.id}
          </Typography>
          <Box>
            <IconButton onClick={onClose} color="default" aria-label="close">
              <CancelIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent 
        ref={dialogContentRef}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          ...(dragActive && {
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              border: '2px dashed #1976d2',
              borderRadius: '4px',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none',
            }
          })
        }}
      >
        {dragActive && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
              backgroundColor: 'white',
              padding: 2,
              borderRadius: 1,
              boxShadow: 3,
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <AttachFileIcon fontSize="large" color="primary" />
            <Typography variant="h6" color="primary">
              Upuść plik, aby dodać załącznik
            </Typography>
          </Box>
        )}
          <>

            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="task details tabs"
                  sx={{ minHeight: '36px' }}
                >
                  <Tab 
                    icon={<AssignmentIcon sx={{ fontSize: '0.85rem' }} />} 
                    label="Szczegóły"
                    {...a11yProps(0)} 
                    sx={{ 
                      minHeight: '36px', 
                      py: 0.5,
                      fontSize: '0.8rem',
                      '& .MuiTab-iconWrapper': { 
                        marginRight: '4px',
                        marginBottom: 0
                      },
                      flexDirection: 'row'
                    }} 
                  />
                  <Tab
                      icon={<CommentIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Komentarze"
                      {...a11yProps(1)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                  <Tab
                      icon={<AttachFileIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Załączniki"
                      {...a11yProps(2)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                  <Tab
                      icon={<TimelineIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Timeline"
                      {...a11yProps(3)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                  <Tab
                      icon={<HistoryIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Historia"
                      {...a11yProps(4)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <Stack spacing={3} sx={{ p: 0.5 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                    {/* All pills in a single row */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                        Status
                      </Typography>
                      <Chip 
                        label={getStatusLabel(task.status || 'TODO')}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(task.status || 'TODO'),
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                        onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                        Priorytet
                      </Typography>
                      <Chip 
                        label={getPriorityLabel(task.priority || 'C')}
                        size="small"
                        sx={{
                          backgroundColor: getPriorityColor(task.priority || 'C'),
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                        onClick={(e) => setPriorityMenuAnchor(e.currentTarget)}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                        Typ
                      </Typography>
                      <Chip 
                        label={getTaskTypeLabel(task.taskType || 'EMAIL')}
                        size="small"
                        sx={{
                          backgroundColor: getTaskTypeColor(task.taskType || 'EMAIL'),
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                        onClick={(e) => setTaskTypeMenuAnchor(e.currentTarget)}
                      />
                    </Box>
                    {task.taskType === 'MEETING' ? (
                      <>
                        {task.startDate && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                              Termin od
                            </Typography>
                            <Chip 
                              ref={startDateChipRef}
                              label={format(new Date(task.startDate), 'dd MMM yyyy HH:mm', { locale: pl })}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                cursor: 'pointer', 
                                fontSize: '0.75rem', 
                                height: '24px',
                                borderRadius: '12px',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                              onClick={() => setStartDatePickerOpen(true)}
                            />
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                            Termin do
                          </Typography>
                          <Chip 
                            ref={endDateChipRef}
                            label={format(new Date(task.endDate), 'dd MMM yyyy HH:mm', { locale: pl })}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              cursor: 'pointer', 
                              fontSize: '0.75rem', 
                              height: '24px',
                              borderRadius: '12px',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                            }}
                            onClick={() => setEndDatePickerOpen(true)}
                          />
                        </Box>
                      </>
                    ) : (
                      task.startDate && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                            Termin
                          </Typography>
                          <Chip 
                            ref={startDateChipRef}
                            label={format(new Date(task.startDate), 'dd MMM yyyy HH:mm', { locale: pl })}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              cursor: 'pointer', 
                              fontSize: '0.75rem', 
                              height: '24px',
                              borderRadius: '12px',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                            }}
                            onClick={() => setStartDatePickerOpen(true)}
                          />
                        </Box>
                      )
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                        Przypisane do użytkownika
                      </Typography>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5, 
                          cursor: 'pointer',
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '16px',
                          padding: '2px 8px',
                          height: '24px',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                        onClick={(e) => setAssignUserMenuAnchor(e.currentTarget)}
                      >
                        <Avatar 
                          sx={{ 
                            width: 18,
                            height: 18,
                            bgcolor: task.assignedToUserId ? 'primary.main' : 'grey.300',
                            fontSize: '0.65rem',
                          }}
                        >
                          {task.assignedToUserId ? getUserInitials(task.assignedToUserId) : <PersonIcon sx={{ fontSize: '0.65rem' }} />}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {getUserFullNameById(task.assignedToUserId)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {titleEditMode ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        fullWidth
                        rows={2}
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        variant="outlined"
                        size="small"
                        label="Tytuł zadania"
                        autoFocus
                        sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                        InputLabelProps={{
                          sx: { bgcolor: 'white', px: 0.5 }
                        }}
                        inputProps={{ maxLength: 255 }}
                        helperText={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Enter - zapisz, Esc - anuluj</span>
                            <span style={{ 
                              color: editedTitle.length >= 255 ? '#d32f2f' : '#666',
                              fontSize: '0.85rem'
                            }}>
                              {editedTitle.length}/255
                            </span>
                          </Box>
                        }
                        error={editedTitle.length > 255}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveTitle();
                          } else if (e.key === 'Escape') {
                            handleCancelTitleEdit();
                          }
                        }}
                      />
                      <IconButton onClick={handleSaveTitle} color="primary" size="small">
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={handleCancelTitleEdit} color="default" size="small">
                        <CancelIcon  fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      margin="none"
                      value={task.title}
                      variant="outlined"
                      label="Tytuł zadania"
                      InputLabelProps={{
                        sx: { bgcolor: 'white', px: 0.5 }
                      }}
                      multiline
                      rows={2}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <EditIcon fontSize="small" sx={{ opacity: 0.5, cursor: 'pointer' }} />,
                        sx: { fontSize: '0.85rem', cursor: 'pointer' }
                      }}
                      onClick={handleTitleClick}
                      sx={{ mb: 1 }}
                    />
                  )}

                  {descriptionEditMode ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={7}
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        variant="outlined"
                        label="Opis zadania"
                        autoFocus
                        InputLabelProps={{
                          sx: { bgcolor: 'white', px: 0.5 }
                        }}
                        inputProps={{ maxLength: 5000 }}
                        sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                        helperText={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Ctrl+Enter - zapisz, Esc - anuluj</span>
                            <span style={{ 
                              color: editedDescription.length >= 5000 ? '#d32f2f' : '#666',
                              fontSize: '0.85rem'
                            }}>
                              {editedDescription.length}/5000
                            </span>
                          </Box>
                        }
                        error={editedDescription.length > 5000}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            handleSaveDescription();
                          } else if (e.key === 'Escape') {
                            handleCancelDescriptionEdit();
                          }
                        }}
                      />
                      <IconButton onClick={handleSaveDescription} color="primary" size="small">
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={handleCancelDescriptionEdit} color="default" size="small">
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      margin="none"
                      value={task.description || 'Brak opisu'}
                      variant="outlined"
                      label="Opis zadania"
                      InputLabelProps={{
                        sx: { bgcolor: 'white', px: 0.5 }
                      }}
                      multiline
                      rows={7}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <EditIcon fontSize="small" sx={{ opacity: 0.5, cursor: 'pointer' }} />,
                        sx: { fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'pre-wrap' }
                      }}
                      onClick={handleDescriptionClick}
                    />
                  )}

                  {/* Contact Assignment Section */}
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                      Przypisane kontakty
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
                      {loading ? (
                        <CircularProgress size={16} />
                      ) : !task?.leads || task.leads.length === 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '16px',
                            padding: '6px 12px',
                            height: '42px',
                            fontSize: '0.75rem',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                          onClick={(e) => setAssignLeadMenuAnchor(e.currentTarget)}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            Brak przypisania do kontaktu
                          </Typography>
                        </Box>
                      ) : (
                        task.leads?.map((lead) => (
                          <Box
                            key={lead.id}
                            sx={{
                              position: 'relative',
                              display: 'inline-block',
                              '&:hover .remove-button': {
                                opacity: 1
                              }
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                borderRadius: '16px',
                                padding: '6px 12px',
                                height: '42px',
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' }
                              }}
                              onClick={() => {
                                if (lead.id) {
                                  setSelectedLeadId(lead.id);
                                  setOpenLeadDetailDialog(true);
                                }
                              }}
                            >
                              <BusinessIcon sx={{ fontSize: '0.65rem', color: 'primary.main' }} />
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                                  {lead.firstName} {lead.lastName}
                                </Typography>
                                {lead.email && (
                                  <Typography variant="body2" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.1 }}>
                                    {lead.email}
                                  </Typography>
                                )}
                                {lead.phone && (
                                  <Typography variant="body2" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.1 }}>
                                    {lead.phone}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box
                              className="remove-button"
                              sx={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: 'error.main',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '&:hover': {
                                  backgroundColor: 'error.dark'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (lead.id) {
                                  handleUnassignLead(lead.id);
                                }
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: '0.7rem' }} />
                            </Box>
                          </Box>
                        ))
                      )}

                      {/* Add Contact Button */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '16px',
                          padding: '6px 12px',
                          height: '42px',
                          minWidth: '40px',
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                        }}
                        onClick={(e) => setAssignLeadMenuAnchor(e.currentTarget)}
                      >
                        <AddIcon sx={{ fontSize: '0.8rem' }} />
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ mb: 2 }}>
                  <MentionInput
                    value={newComment}
                    onChange={handleCommentChange}
                    placeholder="Dodaj komentarz... (użyj @ aby wspomnieć użytkownika)"
                    rows={3}
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      size="small"
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}
                    >
                      Dodaj komentarz
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {loadingComments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={16} />
                  </Box>
                ) : comments.length === 0 ? (
                  <Typography align="center" color="text.secondary" variant="caption" sx={{ py: 0.5, display: 'block' }}>
                    Brak komentarzy
                  </Typography>
                ) : (
                  <>
                    <List dense sx={{ py: 0, overflow: 'auto' }}>
                      {comments
                        .slice(0, showAllComments ? comments.length : 4)
                        .map((comment, index) => (
                          <React.Fragment key={comment.id}>
                            <Box 
                              data-comment-id={comment.id}
                              sx={{ 
                                mb: 0.5, 
                                py: 0.5, 
                                backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.08)',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                overflow: 'hidden'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  mb: 0.25,
                                  cursor: 'pointer',
                                  backgroundColor: 'rgba(63, 81, 181, 0.08)',
                                  px: 1,
                                  py: 0.5
                                }}
                                onClick={() => comment.id && toggleCommentExpand(comment.id)}
                              >
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '0.75rem', 
                                    fontStyle: 'italic',
                                    color: 'rgba(0, 0, 0, 0.7)'
                                  }}
                                >
                                  Użytkownik <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : ''}</Box> dodał komentarz - {format(new Date(comment.createdAt || ''), 'dd MMM yyyy HH:mm', { locale: pl })}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  title={expandedComments[comment.id || 0] ? "Zwiń" : "Rozwiń"}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the parent onClick
                                    comment.id && toggleCommentExpand(comment.id);
                                  }}
                                  sx={{ p: 0.25, ml: 0.5 }}
                                >
                                  <KeyboardArrowDownIcon 
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      transform: expandedComments[comment.id || 0] ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.3s'
                                    }} 
                                  />
                                </IconButton>
                              </Box>
                              {expandedComments[comment.id || 0] && (
                                <Box sx={{ px: 1, py: 0.5 }}>
                                  {editingCommentId === comment.id ? (
                                    <>
                                      <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={editedCommentText}
                                        onChange={(e) => setEditedCommentText(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }}
                                      />
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="primary"
                                          onClick={handleSaveComment}
                                          disabled={!editedCommentText.trim()}
                                          startIcon={<SaveIcon fontSize="small" />}
                                          sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}
                                        >
                                          Zapisz
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={handleCancelEditComment}
                                          startIcon={<CancelIcon fontSize="small" />}
                                          sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}
                                        >
                                          Anuluj
                                        </Button>
                                      </Box>
                                    </>
                                  ) : (
                                    <>
                                      <MentionText 
                                        text={comment.description || ''}
                                        variant="caption"
                                        sx={{ fontSize: '0.8rem' }}
                                      />
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                        <IconButton 
                                          size="small" 
                                          color="primary"
                                          onClick={() => handleEditComment(comment)}
                                          sx={{ p: 0.25 }}
                                        >
                                          <CreateIcon sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                        <IconButton 
                                          size="small" 
                                          color="error"
                                          onClick={() => comment.id && handleDeleteComment(comment.id)}
                                          sx={{ p: 0.25 }}
                                        >
                                          <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </React.Fragment>
                      ))}
                    </List>
                    {comments.length > 4 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Button 
                          size="small" 
                          onClick={() => setShowAllComments(!showAllComments)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {showAllComments ? 'Pokaż mniej' : 'Zobacz więcej'}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Box sx={{ mb: 0.5, mt: 2 }}>
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="raised-button-file"
                    type="file"
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                  />
                  <label htmlFor="raised-button-file">
                    <Button
                      variant="contained"
                      component="span"
                      size="small"
                      startIcon={<AttachFileIcon sx={{ fontSize: '0.85rem' }} />}
                      disabled={isUploading}
                      sx={{ py: 0.5, px: 1, fontSize: '0.7rem' }}
                    >
                      {isUploading ? 'Dodawanie...' : 'Dodaj załącznik'}
                    </Button>
                  </label>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <FileList
                  files={files}
                  isLoading={loadingFiles}
                  onDownload={handleDownloadFile}
                  onDelete={handleDeleteFile}
                  onRestore={restoreFile}
                  compact={true}
                  enablePreview={true}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                {taskId && <TaskStatusTimeline taskId={taskId} />}
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                {taskId && <TaskHistory taskId={taskId} />}
              </TabPanel>
            </Box>
          </>


          {/* Informacje o utworzeniu/aktualizacji - przeniesione na dół dialogu */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1, borderTop: '1px solid #eee', fontSize: '0.75rem', px: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              <Box component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>Utworzono:</Box> 
              {task.createdAt ? format(new Date(task.createdAt), 'dd MMM yyyy HH:mm', { locale: pl }) : 'Brak daty'}
            </Typography>
            {task.updatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                <Box component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>Zaktualizowano:</Box> 
                {format(new Date(task.updatedAt), 'dd MMM yyyy HH:mm', { locale: pl })}
              </Typography>
            )}
          </Box>
      </DialogContent>

      {/* Lead Contact Dialog */}
      <LeadContactDialog
        open={openLeadDialog}
        onClose={handleCloseLeadDialog}
        lead={null}
        onSave={handleSaveLead}
        isSubmitting={isSubmittingLead}
      />

      {/* Lead Detail Dialog */}
      <LeadDetailDialog
        open={openLeadDetailDialog}
        leadId={selectedLeadId}
        onClose={() => {
          setOpenLeadDetailDialog(false);
          setSelectedLeadId(null);
        }}
        initialTab={0}
      />

    </Dialog>
  );
};

export default TaskDetailDialog;
